import { Button, Divider, IconButton, Stack, Typography } from "@mui/material";
import { format } from "date-fns";
import { useMemo } from "react";
import { PatientSubscription, ProductCategories } from "src/api/types";
import WhiteCard from "src/shared/components/WhiteCard";
import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import { useIsMobile } from "src/hooks/useIsMobile";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { usePatientLocale } from "src/hooks/usePatientLocale";
import { enUS } from "date-fns/locale";
import { MedicationIcon } from "src/shared/components/MedicationIcon";
interface SubscriptionCardProps {
  subscription: PatientSubscription;
  handleReactivation?: (sub: number) => void;
}

const SubscriptionCard = ({
  subscription,
  handleReactivation,
}: SubscriptionCardProps) => {
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const page = "subscriptions.card";
  const locale = usePatientLocale();

  const isCanceled = useMemo(
    () => subscription.status === "canceled",
    [subscription.status],
  );

  const refillDate = useMemo(() => {
    if (subscription.status === "canceled") return null;
    if (subscription?.current_period_end == null) return null;

    const dateFormat =
      locale === enUS ? "EEEE, MMMM dd, yyyy" : "EEEE, dd 'de' MMMM, yyyy";

    return format(new Date(subscription.current_period_end), dateFormat, {
      locale,
    });
  }, [locale, subscription.current_period_end, subscription.status]);

  const scheduledForCancellationDate = useMemo(() => {
    if (subscription?.scheduled_for_cancellation_at == null) return null;
    const dateFormat =
      locale === enUS ? "EEEE, MMMM dd, yyyy" : "EEEE, dd 'de' MMMM, yyyy";

    return format(
      new Date(subscription.scheduled_for_cancellation_at),
      dateFormat,
      {
        locale,
      },
    );
  }, [locale, subscription.scheduled_for_cancellation_at]);

  const cancellationDate = useMemo(() => {
    if (subscription?.canceled_at == null) return null;
    const dateFormat =
      locale === enUS ? "EEEE, MMMM dd, yyyy" : "EEEE, dd 'de' MMMM, yyyy";

    return format(new Date(subscription.canceled_at), dateFormat, {
      locale,
    });
  }, [locale, subscription.canceled_at]);

  const showEditOrders = useMemo(
    () =>
      !scheduledForCancellationDate &&
      refillDate &&
      subscription.product_category.name === ProductCategories.WeightLoss &&
      subscription.status !== "canceled",
    [
      refillDate,
      scheduledForCancellationDate,
      subscription.product_category.name,
      subscription.status,
    ],
  );

  return (
    <WhiteCard direction={isMobile ? "column" : "row"} gap={2}>
      <Stack width="100%" gap={1}>
        <Stack
          direction="row"
          alignItems="center"
          gap={isMobile ? 0 : 4}
          width="100%"
        >
          <MedicationIcon
            name={subscription.product.name}
            slug={subscription.product.slug}
          />
          <Stack>
            <Typography fontWeight={600}>
              {t(
                `medications.${subscription.product.display_name?.toLowerCase()}.capitalized`,
                subscription.product.name,
              )}
            </Typography>
            {isCanceled ? null : (
              <Typography>
                {t(`${page}.subtext`, "View subscription detail")}
              </Typography>
            )}
          </Stack>
          {!isMobile ? (
            <Stack>
              {isCanceled ? null : (
                <Typography variant="subtitle2">
                  {t(
                    `${page}.refillInterval`,
                    `Ships every {{refillText}} month(s)`,
                    {
                      refillText: subscription.price.number_of_months,
                    },
                  )}
                </Typography>
              )}
              {refillDate && scheduledForCancellationDate == null ? (
                <Typography variant="subtitle1">
                  {t(`${page}.nextShipment`, "Next shipment")}{" "}
                  <span style={{ color: "#000000", fontWeight: 600 }}>
                    {refillDate}
                  </span>
                </Typography>
              ) : scheduledForCancellationDate && cancellationDate == null ? (
                <Typography variant="subtitle1">
                  {t(
                    `${page}.scheduled-for-cancellation`,
                    "Scheduled for cancellation",
                  )}{" "}
                  <span style={{ color: "#000000", fontWeight: 600 }}>
                    {scheduledForCancellationDate}
                  </span>
                </Typography>
              ) : cancellationDate ? (
                <Typography variant="subtitle1">
                  {t(`${page}.canceled-on`, "Canceled on")}{" "}
                  <span style={{ color: "#000000", fontWeight: 600 }}>
                    {cancellationDate}
                  </span>
                </Typography>
              ) : null}
            </Stack>
          ) : null}

          {isCanceled && handleReactivation ? (
            <Button
              variant="secondary"
              sx={{ ml: "auto" }}
              onClick={() => handleReactivation(subscription.id)}
            >
              {t(`${page}.rectivate`, "Reactivate")}
            </Button>
          ) : (
            <IconButton
              size="small"
              sx={{ ml: "auto" }}
              onClick={() => navigate(`${subscription.id}`)}
            >
              <NavigateNextIcon fontSize="large" />
            </IconButton>
          )}
        </Stack>
        {!isMobile ? null : (
          <Stack>
            {isCanceled ? null : (
              <Typography variant="subtitle2">
                {t(
                  `${page}.refillInterval`,
                  `Ships every {{refillText}} month(s)`,
                  {
                    refillText: subscription.price.number_of_months,
                  },
                )}
              </Typography>
            )}
            {refillDate && scheduledForCancellationDate == null ? (
              <Typography variant="subtitle1">
                {t(`${page}.nextShipment`, "Next shipment")}{" "}
                <span style={{ color: "#000000", fontWeight: 600 }}>
                  {refillDate}
                </span>
              </Typography>
            ) : scheduledForCancellationDate && cancellationDate == null ? (
              <Typography variant="subtitle1">
                {t(
                  `${page}.scheduled-for-cancellation`,
                  "Scheduled for cancellation at",
                )}{" "}
                <span style={{ color: "#000000", fontWeight: 600 }}>
                  {scheduledForCancellationDate}
                </span>
              </Typography>
            ) : cancellationDate ? (
              <Typography variant="subtitle1">
                {t(`${page}.canceled-on`, "Canceled on")}{" "}
                <span style={{ color: "#000000", fontWeight: 600 }}>
                  {cancellationDate}
                </span>
              </Typography>
            ) : null}
          </Stack>
        )}

        {!showEditOrders ? null : (
          <>
            {" "}
            <Divider
              sx={{
                backgroundColor: "#E4E7EE",
                mt: 2,
                width: "100%",
              }}
            />
            <Stack direction="row" mt={1}>
              <Stack gap={0.5}>
                <Typography variant="subtitle2">
                  {t(`${page}.additional-options`, "Additional Options")}
                </Typography>
                <Typography>
                  {t(`${page}.edit-upcoming-orders`, "Edit upcoming orders")}
                </Typography>
              </Stack>
              <IconButton
                size="small"
                sx={{ ml: "auto" }}
                onClick={() => navigate(`${subscription.id}/manage`)}
              >
                <NavigateNextIcon fontSize="large" />
              </IconButton>
            </Stack>
          </>
        )}
      </Stack>
    </WhiteCard>
  );
};

export default SubscriptionCard;
